const emptyRule = (target = 'debit') => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  target,
  content: '',
  accountCardId: '',
  vatPercentage: null,
  autoProcess: null,
  sortCodeId: '',
});

const normalizeVat = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeAutoProcess = (value) => {
  if (value === true || value === false) return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const normalizeSortCodeId = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).trim();
};

export const createEmptyOcrRoutingConfig = (defaultTarget = 'debit') => ({
  enabled: false,
  rules: [],
  defaultTarget,
});

const normalizeRule = (rule, fallbackTarget = 'debit') => ({
  id: rule?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  target: rule?.target === 'credit' ? 'credit' : 'debit',
  content: rule?.content ?? '',
  accountCardId: rule?.accountCardId != null ? String(rule.accountCardId) : '',
  vatPercentage: normalizeVat(rule?.vatPercentage),
  autoProcess: normalizeAutoProcess(rule?.autoProcess),
  sortCodeId: normalizeSortCodeId(rule?.sortCodeId),
});

const normalizeRuleForSave = (rule, forcedTarget) =>
  normalizeRule(
    forcedTarget
      ? { ...rule, target: forcedTarget }
      : rule,
    forcedTarget ?? 'debit'
  );

const isMeaningfulHtml = (html) => {
  if (!html || typeof html !== 'string') return false;
  const text = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
  return text.length > 0;
};

const isMeaningfulRule = (rule) =>
  isMeaningfulHtml(rule.content) && !!String(rule.accountCardId || '').trim();

const mapApiRule = (rule, defaultTarget = 'debit') =>
  normalizeRule(
    {
      id: rule?.id ?? rule?.Id,
      target: rule?.target ?? rule?.Target,
      content: rule?.content ?? rule?.Content,
      accountCardId:
        rule?.accountCardId ??
        rule?.AccountCardId ??
        rule?.routeAccountCardId ??
        rule?.RouteAccountCardId,
      vatPercentage: rule?.vatPercentage ?? rule?.VatPercentage,
      autoProcess: rule?.autoProcess ?? rule?.AutoProcess,
      sortCodeId: rule?.sortCodeId ?? rule?.SortCodeId,
    },
    defaultTarget
  );

/**
 * Load API response into editor state.
 */
export const parseOcrRoutingFromApi = (apiCard, defaultTarget = 'debit') => {
  if (!apiCard) {
    return createEmptyOcrRoutingConfig(defaultTarget);
  }

  const enabled = !!(apiCard.ocrRoutingEnabled ?? apiCard.OcrRoutingEnabled);
  const apiRules = apiCard.ocrRoutingRules ?? apiCard.OcrRoutingRules;

  if (Array.isArray(apiRules)) {
    return {
      enabled,
      rules: apiRules.map((r) => mapApiRule(r, defaultTarget)),
    };
  }

  return {
    enabled: false,
    rules: [],
  };
};

/**
 * Serialize editor state for API.
 */
export const serializeOcrRoutingForApi = (config, forcedTarget = null) => {
  const allRules = (config?.rules ?? []).map((r) => normalizeRuleForSave(r, forcedTarget));
  const activeRules = allRules.filter((r) => isMeaningfulRule(r));
  const preservedRules = allRules.filter(
    (r) => isMeaningfulHtml(r.content) || String(r.accountCardId || '').trim()
  );

  const toApiRule = (rule, index) => ({
    id: rule.id,
    target: rule.target,
    content: rule.content,
    accountCardId: rule.accountCardId,
    vatPercentage: normalizeVat(rule.vatPercentage),
    autoProcess: normalizeAutoProcess(rule.autoProcess),
    sortCodeId: normalizeSortCodeId(rule.sortCodeId) || null,
    sortOrder: index,
  });

  if (!config?.enabled) {
    return {
      ocrRoutingEnabled: false,
      ocrRoutingRules: preservedRules.map(toApiRule),
    };
  }

  return {
    ocrRoutingEnabled: true,
    ocrRoutingRules: activeRules.map(toApiRule),
  };
};

export { emptyRule, isMeaningfulHtml, isMeaningfulRule };
